import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Avatar } from '../components/shared/Avatar';
import StateObserver, { user } from 'src/StateObserver';
import * as Profile from 'src/replicant/profile';
import { GCInstant as platform } from 'src/lib/gcinstant';
import {
  generateRandomName,
  getRandomItemFromArray,
} from 'src/replicant/utils/random';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import { TextInput } from '../components/shared/TextInput';
import { profileAvatarAssets } from 'src/replicant/creatives/assets';
import { track } from 'src/lib/analytics/utils';
import Scroll from '../components/shared/Scroll';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { AssetGroup } from '@play-co/timestep-core';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import EditProfileV2AvatarItem from '../profile/components/EditProfileV2AvatarItem';
import { getPlayerProfileAvatarFrame } from 'src/replicant/profile/profile.getters';
import { isEnteringFromInviteV2 } from '../friends/addFriendsV2.sequence';

const assets = {
  clear: 'assets/ui/shared/icons/icon_clearinput.png',
};

const ui = uiConfig.buruburu.profile;

type AvatarRow = [
  EditProfileV2AvatarItem,
  EditProfileV2AvatarItem?,
  EditProfileV2AvatarItem?,
];

export default class PopupEditProfileV2 extends PopupBasic<
  (canceled: boolean) => void
> {
  private inputField: TextInput;
  private avatar: Avatar;
  private scroll: Scroll<AvatarRow>;
  private items: EditProfileV2AvatarItem[] = [];

  private get currentAvatar() {
    return this._currentAvatar;
  }
  private set currentAvatar(avatar: string) {
    this._currentAvatar = avatar;
    this.avatar.updateImage(avatar, !!this.currentAvatar.length);
  }
  private _currentAvatar: string = '';

  private buttonOkay: ButtonScaleViewWithText;

  constructor(opts: { superview: View; close: (canceled: boolean) => void }) {
    super({
      superview: opts.superview,
      close: () => {
        opts.close(true);
      },
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 795,
      darkerBg: false,
      tracking: { feature: 'profile', $subFeature: 'popup-editProfile' },
      skipMessage: true,
    });

    this.buttonClose.tracking.$subFeature = 'popup-editProfile-close';

    this.avatar = new Avatar({
      superview: this.box,
      width: 105,
      height: 106,
      dropShadow: true,
      x: 95,
      y: 142,
    });

    // Input field panel
    const inputPanel = new ImageScaleView({
      ...uiConfig.popups.scrollBox,
      superview: this.box,
      x: 165,
      y: 125,
      width: 382,
      height: 68,
      anchorY: 34,
      offsetY: -34,
    });

    const paddingTop = 7;
    const paddingLeft = 25;

    this.inputField = new TextInput({
      superview: inputPanel,
      width: inputPanel.style.width - 6 - paddingLeft - 50,
      height: inputPanel.style.height - 6 - paddingTop,
      inputStyle: {
        color: '#FFFFFF',
        fontWeight: 'bold',
        fontSize: '25px',
      },
      x: 3 + paddingLeft,
      y: 3 + paddingTop,
    });

    // Clear input button
    new ButtonScaleView({
      superview: inputPanel,
      x: inputPanel.style.width - 32,
      y: inputPanel.style.height * 0.5 + 2,
      image: assets.clear,
      width: 30,
      height: 30,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.sfx.play('cancel');
        this.inputField.text = '';
        this.inputField.validate();
      },
      tracking: {
        feature: 'profile',
        $subFeature: 'popup-editProfile-clear-input',
      },
    });

    // Input field requirement message
    new LangBitmapFontTextView({
      color: '#D89B62',
      size: 20,
      font: 'Body',
      superview: this.box,
      localeText: () => i18n('profile.edit.maxLengthV2'),
      x: inputPanel.style.x + 6,
      y: inputPanel.style.y + 48,
      width: inputPanel.style.width,
      height: 27,
    });

    const scrollWidth = 488;

    // Avatar scroll-box
    this.scroll = new Scroll<AvatarRow>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      margin: 15,
      rect: {
        x: (this.box.style.width - scrollWidth) * 0.5,
        y: 218,
        width: scrollWidth,
        height: 419,
      },
    });

    this.buttonOkay = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      ...ui.button,
      x: this.box.style.width * 0.5,
      y: this.box.style.height - 86,
      localeText: () => i18n('profile.edit.save'),
      onClick: async () => {
        sounds.sfx.play('ok');

        this.setInputFieldVisible(false);

        const name = this.inputField.text;
        const photo = this.currentAvatar;

        const { photo: currentPhoto, name: currentName } =
          user().publicProfile ?? {};

        await StateObserver.invoke.updatePublicProfile({
          name,
          photo,
          enteredFromInviteV2: isEnteringFromInviteV2(),
          originFeature: platform.entryData?.originFeature,
          originSubFeature: platform.entryData?.originSubFeature,
        });

        track('InGameProfileUpdate', {
          imageId: photo,
          isLineName: name === platform.playerName,
          imageUpdated: photo !== currentPhoto,
          nameUpdated: name !== currentName,
        });

        return opts.close(false);
      },
      tracking: { feature: 'profile', $subFeature: 'popup-editProfile-ok' },
    });
  }

  public init({ hideCloseButton = false }: { hideCloseButton?: boolean }) {
    super.init({
      title: i18n('profile.edit.titleV2'),
      message: i18n('profile.edit.notice'),
    });

    if (hideCloseButton) {
      this.buttonClose.removeFromSuperview();
    }

    const { profile, publicProfile } = user();
    const { name, photo } = publicProfile ?? {};

    // Show frame according to master league status.
    this.avatar.setFrame(getPlayerProfileAvatarFrame(user()));

    /**
     * Fallback to LINE (private profile) name when editing
     * the public profile for the first time.
     */
    this.inputField.text =
      name ??
      profile?.name ??
      generateRandomName(StateObserver.getLocale(), Math.random);

    const profileAvatars = Object.keys(profileAvatarAssets);

    // Reset current avatar cache
    this.currentAvatar =
      photo ??
      profile?.photo ??
      getRandomItemFromArray(
        Object.keys(profileAvatarAssets).map((id) =>
          StateObserver.replicant.getChatbotAssetUrl(id),
        ),
        Math.random,
      );

    const [minLength, maxLength] = Profile.ruleset.nameLength;

    this.inputField.on('InputValidated', () => this.updateCTAState());

    // Reset input field.
    this.inputField.updateOpts({
      minLength,
      maxLength,
      placeholder: i18n('profile.edit.placeHolder'),
    });

    if (hideCloseButton) {
      this.buttonOkay.updateOpts({
        x: this.box.style.width / 2,
      });
    }

    // Add available avatars to the scroll-box.
    this.scroll.setItems(
      [
        { url: platform.playerPhoto },
        ...profileAvatars.map((id) => ({
          url: StateObserver.replicant.getChatbotAssetUrl(id),
        })),
      ].reduce((result, _, i, array) => {
        if (i % 3 === 0) {
          result.push(array.slice(i, i + 3) as AvatarRow);
        }
        return result;
      }, [] as AvatarRow[]),
    );

    // Validate the initial input value which could be LINE name fallback.
    this.inputField.validate();

    // Reset selection
    this.updateSelection(true);
  }

  private setInputFieldVisible(visible: boolean) {
    // Wait animationDuration before showing/hiding the input field.
    waitForItPromise(animDuration).then(() => {
      this.inputField.style.visible = visible;
    });
  }

  private createItem(superview: View, _: number, avatarItems: AvatarRow) {
    const row = superview.addSubview(
      new FlexBoxView({
        width: superview.style.width,
        height: 136,
        flex: {
          paddingVertical: 10,
          paddingHorizontal: 22,
        },
      }),
    );

    avatarItems.forEach((item) => {
      if (item) {
        const { url } = item;

        this.items.push(
          new EditProfileV2AvatarItem({
            superview: row,
            url,
            onClick: async () => {
              if (url === this.currentAvatar) return;
              sounds.sfx.play('ok');
              this.currentAvatar = url;
              this.updateSelection();
            },
          }),
        );
      }
    });

    return row;
  }

  private updateSelection(instant = false) {
    this.updateCTAState();
    this.items.forEach((item) => {
      item.updateSelected(this.currentAvatar, instant);
    });
  }

  private updateCTAState() {
    this.buttonOkay.setDisabled(
      !this.inputField.isValid || !this.currentAvatar,
    );
  }

  protected getAssetsGroup(): AssetGroup | null {
    return new AssetGroup(
      AssetGroup.constructURLs([
        ...Object.values(assets),
        ...EditProfileV2AvatarItem.assets,
      ]),
    );
  }
}
